import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tag, ArrowRight, ShoppingBag, Search } from 'lucide-react';
import { Footer } from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ICONS = {
  'academia-e-esporte': '🏋️',
  'beleza-feminina': '💅',
  'beleza-masculina': '✂️',
  'estetica-e-bem-estar': '🧖',
  'moda-e-acessorios': '👗',
  'infantil-e-rn': '👶',
  'saude': '🏥',
  'saude-veterinaria': '🐾',
  'pet-shop': '🐕',
  'casa-e-decoracao': '🏠',
  'construcao-e-reforma': '🔨',
  'eletronicos-e-tecnologia': '📱',
  'veiculos-eletricos': '⚡',
  'carro-e-motos': '🚗',
  'gastronomia-e-bebidas': '🍽️',
  'manutencao-e-reparos': '🔧',
  'lazer-e-diversao': '🎭',
};

const CATEGORY_COLORS = [
  'from-rose-500 to-pink-600',
  'from-violet-500 to-purple-600',
  'from-blue-500 to-indigo-600',
  'from-cyan-500 to-teal-600',
  'from-emerald-500 to-green-600',
  'from-amber-500 to-orange-600',
  'from-red-500 to-rose-600',
  'from-fuchsia-500 to-pink-600',
  'from-sky-500 to-blue-600',
];

export default function CategoriesPage() {
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'Categorias de Cupons - ClubdoCupom.com';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', 'Explore todas as categorias de cupons de desconto do ClubdoCupom.com. Academias, beleza, gastronomia, tecnologia e muito mais.');

    axios.get(`${BACKEND_URL}/api/categories`)
      .then(res => setCategories(res.data || []))
      .catch(() => setCategories([]))
      .finally(() => setLoading(false));

    return () => { document.title = 'ClubdoCupom.com - O Canal Oficial do Desconto Direto'; };
  }, []);

  const filtered = search
    ? categories.filter(c => c.name.toLowerCase().includes(search.toLowerCase()))
    : categories;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navbar */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-slate-200/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2">
            <span className="text-xl sm:text-2xl font-black text-slate-900">ClubdoCupom</span>
          </Link>
          <Link to="/">
            <Button className="bg-amber-500 hover:bg-amber-600 text-white font-bold py-2 px-4 sm:px-6 rounded-lg text-sm sm:text-base" data-testid="back-home-btn">
              Inicio
            </Button>
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="bg-slate-900 py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-sm px-4 py-1 mb-4">
            <Tag className="h-3.5 w-3.5 mr-1.5" /> Todas as Categorias
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-black text-white mb-4" data-testid="categories-title">
            Categorias de Cupons
          </h1>
          <p className="text-base sm:text-lg text-slate-400 max-w-2xl mx-auto mb-8">
            Encontre descontos exclusivos organizados por categoria. Escolha a sua e economize.
          </p>

          {/* Search */}
          <div className="max-w-md mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar categoria..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500/50 text-base"
              data-testid="categories-search"
            />
          </div>
        </div>
      </section>

      {/* Categories Grid */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-16">
              <div className="animate-spin h-8 w-8 border-4 border-amber-500 border-t-transparent rounded-full mx-auto mb-4" />
              <p className="text-slate-500">Carregando categorias...</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16">
              <ShoppingBag className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 text-lg">Nenhuma categoria encontrada</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="categories-grid">
              {filtered.map((cat, i) => (
                <Link key={cat.slug} to={`/categorias/${cat.slug}`} data-testid={`category-card-${cat.slug}`}>
                  <Card className="group border-0 shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer hover:-translate-y-1">
                    <div className={`bg-gradient-to-br ${CATEGORY_COLORS[i % CATEGORY_COLORS.length]} p-1`}>
                      <CardContent className="bg-white rounded-t-none p-6">
                        <div className="flex items-start justify-between mb-3">
                          <span className="text-3xl">{CATEGORY_ICONS[cat.slug] || '📦'}</span>
                          {cat.coupon_count > 0 && (
                            <Badge className="bg-green-100 text-green-700 border-green-200 text-xs font-bold">
                              {cat.coupon_count} {cat.coupon_count === 1 ? 'cupom' : 'cupons'}
                            </Badge>
                          )}
                        </div>
                        <h2 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-amber-600 transition-colors">
                          {cat.name}
                        </h2>
                        <p className="text-sm text-slate-500 line-clamp-2 mb-4">{cat.description}</p>
                        <div className="flex items-center text-amber-600 text-sm font-semibold group-hover:gap-2 transition-all">
                          Ver cupons <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </CardContent>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
